import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { TaskRedistributionService } from '../tokens/task-redistribution.token';
import { TaskTransferRequest, WorkflowTask } from '../models/task.model';
import { API_PATHS } from '../config/api-paths';
import { extractBackendErrorMessage } from '../util/backend-error';

/**
 * REST-backed implementation of `TaskRedistributionService` (S-F / EM-660 option b, EM-661).
 *
 * Calls the ROLE_MASTER_ADMIN-gated `TaskRedistributionResource` at
 * `/services/embedded/api/task-redistribution`. Authenticated via `authInterceptor`.
 *
 * Rule 09 — `reassign` surfaces backend error messages. Rule 10 — no mock fallback.
 */
@Injectable()
export class TaskRedistributionRestService implements TaskRedistributionService {
  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);

  private get base(): string {
    return `${API_PATHS.EMBEDDED_API_BASE}/task-redistribution`;
  }

  reassign(taskId: string, body: TaskTransferRequest): Observable<WorkflowTask> {
    return this.http.post<WorkflowTask>(`${this.base}/${taskId}/reassign`, body).pipe(
      catchError((err: HttpErrorResponse) =>
        throwError(() => ({
          code: extractBackendErrorMessage(
            err,
            this.transloco.translate('masterAdmin.workDistribution.errors.reassignFailed'),
            key => this.transloco.translate(key),
          ),
        })),
      ),
    );
  }
}
